/**
 * Info popover for pricing setting fields — detailed plain-language help + examples.
 */

import React from "react";
import { Popover, Typography, Divider, Button } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import styled from "styled-components";

const { Text, Paragraph } = Typography;

const HelpRoot = styled.div`
  max-width: 440px;
`;

const ExampleBox = styled.pre`
  margin: 8px 0 0;
  padding: 10px 12px;
  background: #f5f5f5;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  font-size: 12px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
`;

/**
 * @param {object} props
 * @param {import('../utils/pricingSettingsHelp.js').PricingFieldHelp|null} props.help
 * @param {string} [props.size] - 'field' | 'section'
 */
const PricingFieldHelpBubble = ({ help, size = "field" }) => {
  if (!help) {
    return null;
  }

  const content = (
    <HelpRoot>
      <Paragraph style={{ marginBottom: 8, fontSize: 13 }}>
        {help.summary}
      </Paragraph>

      {help.whenApplies && (
        <>
          <Text strong style={{ fontSize: 12 }}>
            When it applies
          </Text>
          <Paragraph
            type="secondary"
            style={{ fontSize: 12, marginTop: 4, marginBottom: 8 }}
          >
            {help.whenApplies}
          </Paragraph>
        </>
      )}

      {help.details?.length > 0 && (
        <>
          <Text strong style={{ fontSize: 12 }}>
            More detail
          </Text>
          {help.details.map((paragraph) => (
            <Paragraph
              key={paragraph.slice(0, 48)}
              type="secondary"
              style={{ fontSize: 12, marginTop: 6, marginBottom: 0 }}
            >
              {paragraph}
            </Paragraph>
          ))}
        </>
      )}

      {help.example && (
        <>
          <Divider style={{ margin: "12px 0 8px" }} />
          <Text strong style={{ fontSize: 12 }}>
            Example
          </Text>
          <ExampleBox>{help.example}</ExampleBox>
        </>
      )}
    </HelpRoot>
  );

  return (
    <Popover
      content={content}
      title={help.title}
      trigger="click"
      placement={size === "section" ? "bottomLeft" : "rightTop"}
    >
      <Button
        type="text"
        size="small"
        aria-label={`Help: ${help.title}`}
        title="Click for detailed help"
        icon={
          <InfoCircleOutlined style={{ fontSize: size === "section" ? 16 : 14 }} />
        }
        style={{ color: "#1890ff", padding: 0, height: "auto", minWidth: "auto" }}
      />
    </Popover>
  );
};

export default PricingFieldHelpBubble;
